// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { LoadingIndicator } from '@/components/navigation-layout';
import { sortDirectionSchema } from '@/constants/sort';
import { useDisclosure } from '@/hooks/use-disclosure';
import { useTimeout } from '@/hooks/use-timeout';
import { LocationFilter } from '@/location/api/get-locations';
import { LocationDrawerContent } from '@/location/components/location-drawer-content';
import { LocationFunctionalStatusReportCard } from '@/location/components/location-functional-status-report-card';
import { LocationMapControlBar } from '@/location/components/location-map-control-bar';
import { LocationOwnershipReportCard } from '@/location/components/location-ownership-report-card';
import { LocationMap } from '@/location/components/map';
import { SiteNotFound } from '@/location/components/site-not-found';
import { Box, Dialog, Drawer, Paper, useTheme } from '@mui/material';
import { createFileRoute } from '@tanstack/react-router';
import { omit } from 'lodash-es';
import { Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { z } from 'zod';

const searchSchema = z.object({
  location: z.string().optional(),
  sort: z.enum(['count', 'name']).optional(),
  direction: sortDirectionSchema.optional(),
});

export const Route = createFileRoute('/_app/dashboard/location')({
  component: LocationDashboardRoute,
  validateSearch: searchSchema,
});

function LocationDashboardRoute() {
  const theme = useTheme();
  const searchParameters = Route.useSearch();
  const navigate = Route.useNavigate();

  const sort = {
    property: searchParameters.sort ?? 'count',
    direction: searchParameters.direction ?? 'desc',
  };

  // Filter for the map (empty filter to show all locations)
  const filter: LocationFilter = {};

  // Disclosure hooks for map functionality
  const map = useDisclosure();
  const emptyLocations = useDisclosure();
  const locationDrawer = useDisclosure(Boolean(searchParameters.location));

  // Timeout for drawer closing
  const drawerTimeout = useTimeout(
    () => navigate({ search: omit(searchParameters, 'location') }),
    theme.transitions.duration.standard
  );

  // Handle clicks from the map bubble
  const handleMapBubbleClick = (key: string) => {
    locationDrawer.open();
    navigate({ search: { ...searchParameters, location: key } });
  };

  const handleCloseDrawer = () => {
    locationDrawer.close();
    drawerTimeout.start();
  };

  return (
    <>
      {/* Location Drawer */}
      <Drawer
        anchor="right"
        open={locationDrawer.isOpen}
        onClose={handleCloseDrawer}
        transitionDuration={theme.transitions.duration.standard}
        PaperProps={{ sx: { width: { xs: '100%', sm: '40rem' } } }}
        sx={{ zIndex: theme.zIndex.modal }}
      >
        {searchParameters.location ? (
          <ErrorBoundary FallbackComponent={SiteNotFound}>
            <Suspense fallback={<LoadingIndicator />}>
              <LocationDrawerContent
                locationKey={searchParameters.location}
                onClose={handleCloseDrawer}
              />
            </Suspense>
          </ErrorBoundary>
        ) : null}
      </Drawer>

      {/* Fullscreen Map Dialog */}
      <Dialog fullScreen open={map.isOpen} onClose={map.close}>
        <Box height="100%" width="100%" position="relative">
          <Suspense fallback={<LoadingIndicator />}>
            <LocationMap
              filter={filter}
              showEmptyLocations={emptyLocations.isOpen}
              onClickLocation={handleMapBubbleClick}
            />
            <LocationMapControlBar
              isFullscreen={map.isOpen}
              showEmptyLocations={emptyLocations.isOpen}
              onExitFullscreen={map.close}
              onEnterFullscreen={map.open}
              onShowEmptyLocations={emptyLocations.open}
              onHideEmptyLocations={emptyLocations.close}
            />
          </Suspense>
        </Box>
      </Dialog>

      {/* Dashboard Grid */}
      <Box
        display="grid"
        gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }}
        gridTemplateRows={{ xs: 'repeat(3, auto)', md: 'auto 1fr' }}
        gap={2}
        flex={1}
      >
        {/* Location Functional Status Report Card */}
        <LocationFunctionalStatusReportCard sort={sort} />

        {/* Location Ownership Report Card */}
        <LocationOwnershipReportCard sort={sort} />

        {/* Location Map */}
        <Box
          sx={{ gridColumn: { xs: 'span 1', md: 'span 2' } }}
          height="40rem"
          width="100%"
          component={Paper}
          variant="outlined"
          overflow="hidden"
          position="relative"
        >
          <Suspense fallback={<LoadingIndicator />}>
            <LocationMap
              filter={filter}
              showEmptyLocations={emptyLocations.isOpen}
              onClickLocation={handleMapBubbleClick}
            />
            <LocationMapControlBar
              isFullscreen={map.isOpen}
              showEmptyLocations={emptyLocations.isOpen}
              onExitFullscreen={map.close}
              onEnterFullscreen={map.open}
              onShowEmptyLocations={emptyLocations.open}
              onHideEmptyLocations={emptyLocations.close}
            />
          </Suspense>
        </Box>
      </Box>
    </>
  );
}
